import React, { useState, createContext, useEffect } from 'react'
import { useSecureFederatedQuery as useQuery } from '@at/sauron-platform-graphql'
import { VehicleChannel } from '../__generated__/fed-graphql-types'
import { isTpf } from '@at/formatter-utils/lib/channel-utils/channel.util'
import { useRetailerStockSearch } from '../utils/retailer-stock'
import { SAVED_SEARCHES_QUERY } from '../patterns/saved-searches/saved-searches.fed.query'
import { PersonalData, SavedSearch } from '../patterns/saved-searches/save-search'
import { useSaveSearchCompare } from '../hooks/saved-search/save-search-compare.hook'
import { useEmailVerification } from '../hooks/saved-search/use-email-verified'
import {
    SavedSearchesQuery_Query,
    SavedSearchesQueryVariables,
} from '../patterns/saved-searches/__generated__/saved-searches.fed.query'
import { useChannel } from '../hooks/use-abtest.hook'
import { useUserState } from '@at/connected-signin'

export type CreateSavedSearchContextType = {
    savedSearchList: ReadonlyArray<SavedSearch>
    savedSearchListError: boolean
    savedSearchListLoading: boolean
    savedSearchListRefetch: () => void
    savedSearchPersonalData: PersonalData
    foundMatchingSavedSearch: boolean
    setSavedSearchList: (savedSearch: ReadonlyArray<SavedSearch>) => void
    userEmailVerificationRefetch: () => Promise<unknown>
    sendVerificationEmail: () => Promise<boolean>
}

export const SavedSearchQueryContext = createContext<CreateSavedSearchContextType>({
    savedSearchList: [],
    savedSearchListError: false,
    savedSearchListLoading: false,
    savedSearchListRefetch: () => {},
    savedSearchPersonalData: { email: undefined, verified: undefined },
    foundMatchingSavedSearch: false,
    setSavedSearchList: () => {},
    userEmailVerificationRefetch: () => Promise.resolve(),
    sendVerificationEmail: () => Promise.resolve(false),
})
SavedSearchQueryContext.displayName = 'SavedSearchContext'

export const SavedSearchContextProvider: React.FC<React.PropsWithChildren<object>> = ({
    children,
}: React.PropsWithChildren<object>) => {
    const channel = useChannel()
    const [userState] = useUserState()
    const [savedSearchList, setSavedSearchList] = useState<ReadonlyArray<SavedSearch>>([])
    const [personalData, setPersonalData] = useState<PersonalData>({ email: undefined, verified: undefined })
    const { foundMatchingSavedSearch } = useSaveSearchCompare(savedSearchList)
    const [isRetailerStockSearch] = useRetailerStockSearch()
    const skipQuery = isTpf(channel) || !userState.userSignedIn || isRetailerStockSearch
    const {
        emailVerified,
        loading: emailVerificationLoading,
        sendVerificationEmail,
        userEmailVerificationRefetch,
    } = useEmailVerification()

    /** Fetch save searches if user is logged in */
    const { data, loading, error, refetch } = useQuery<SavedSearchesQuery_Query, SavedSearchesQueryVariables>(
        SAVED_SEARCHES_QUERY,
        {
            variables: { channel: channel as unknown as VehicleChannel },
            skip: skipQuery,
            fetchPolicy: 'no-cache',
        }
    )

    useEffect(() => {
        if (!data || emailVerificationLoading) return
        try {
            const results = data.user?.savedSearches.results || []
            const mappedResults = results
                .filter((result) => result.searchCriteria!)
                .map((result) => ({
                    ...result,
                    searchCriteria: result.searchCriteria!,
                    canonicalSearchValues: JSON.parse(result.canonicalSearchValues.toString()),
                }))
            setSavedSearchList(mappedResults)
            setPersonalData({ email: data?.user?.email, verified: emailVerified })
        } catch (error) {
            const errorMessage = (error as Record<string, string>).message
            console.error(`Error parsing saved searches: ${errorMessage}`)
        }
    }, [data, emailVerificationLoading, emailVerified])

    const SavedSearchValues = {
        savedSearchList,
        savedSearchListError: !!error,
        savedSearchListLoading: loading,
        savedSearchListRefetch: refetch,
        savedSearchPersonalData: personalData,
        foundMatchingSavedSearch: foundMatchingSavedSearch,
        setSavedSearchList,
        userEmailVerificationRefetch,
        sendVerificationEmail,
    }
    return <SavedSearchQueryContext.Provider value={SavedSearchValues}>{children}</SavedSearchQueryContext.Provider>
}
